import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { format } from "date-fns";
import {
  FileText, Download, Send, User, CheckCircle, Clock, AlertCircle,
  CreditCard, Trash2, Edit, Receipt
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  paid: { label: 'Paid', color: 'bg-green-500', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-500', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-400', icon: Clock }
};

export const InvoiceCard = ({
  invoice,
  isSelected,
  onToggleSelect,
  onPayOnline,
  onSendInvoice,
  onDownloadPdf,
  onUpdateStatus,
  onEditInvoice,
  onOpenCreditNotes,
  onDeleteInvoice
}) => {
  const statusConfig = STATUS_CONFIG[invoice.status];
  const StatusIcon = statusConfig?.icon || FileText;

  return (
    <Card className="dashboard-card hover:border-primary/30 transition-colors">
      <CardContent className="py-4 px-3 sm:px-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(invoice.id)}
            className="h-4 w-4 rounded border-white/20 bg-secondary mt-1 sm:mt-0"
          />
          
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 items-start sm:items-center">
            <div>
              <p className="font-mono font-bold text-primary text-sm sm:text-base">{invoice.invoice_number}</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(invoice.issued_date), "dd MMM yyyy")}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground hidden sm:block" />
              <div>
                <p className="font-medium text-sm">{invoice.client_name}</p>
                {invoice.client_company && (
                  <p className="text-xs text-muted-foreground">{invoice.client_company}</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-base sm:text-lg font-bold">${invoice.total.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">
                {invoice.booking_ids.length} booking(s)
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`${statusConfig?.color} text-xs`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig?.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Due: {format(new Date(invoice.due_date), "dd MMM")}
              </span>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 justify-start lg:justify-end flex-wrap">
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPayOnline(invoice)}
                  data-testid={`pay-online-${invoice.id}`}
                  title="Pay Online"
                  className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                >
                  <CreditCard className="h-4 w-4 mr-1" />
                  Pay
                </Button>
              )}
              {invoice.status === 'draft' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSendInvoice(invoice.id)}
                  data-testid={`send-${invoice.id}`}
                  title="Send to client"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDownloadPdf(invoice.id, invoice.invoice_number)}
                data-testid={`download-${invoice.id}`}
                title="Download PDF"
              >
                <Download className="h-4 w-4" />
              </Button>
              
              <Select
                value={invoice.status}
                onValueChange={(value) => onUpdateStatus(invoice.id, value)}
              >
                <SelectTrigger className="w-[120px] h-8 text-xs bg-secondary/50 border-black/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              {invoice.status !== 'paid' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditInvoice(invoice)}
                  data-testid={`edit-${invoice.id}`}
                  title="Edit/Revise invoice"
                  className="text-primary hover:text-primary/80 hover:bg-primary/10"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              
              {invoice.status === 'paid' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onOpenCreditNotes(invoice)}
                  data-testid={`credit-note-${invoice.id}`}
                  title="Create Credit Note"
                  className="text-green-500 hover:text-green-400 hover:bg-green-500/10"
                >
                  <Receipt className="h-4 w-4" />
                </Button>
              )}
              
              {invoice.status !== 'paid' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteInvoice(invoice.id)}
                  data-testid={`delete-${invoice.id}`}
                  title="Delete invoice"
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
